import { useEffect, Dispatch, SetStateAction } from "react";
import { useRouter } from "next/router";

import { EditorContent as ReadOnlyEditorContent } from "@tiptap/react";

import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import ReadAloudButton, {
  ButtonTypes,
} from "@/view/ReadAloudButton/ReadAloudButton";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import ALPHABET from "@/view/constants/alphabet";
import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import { listenModeHighlightDecoratorPluginKey } from "@/view/TiptapArticleBody/extensions/ListenModeHighlightDecorator";

import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import useMultipleChoiceEditor from "@/view/QuizQuestion/useMultipleChoiceEditor/";
import { Letter } from "@/view/QuizQuestion/constants";
import {
  choiceLetterCss,
  choiceTextCss,
  containerCss,
  multipleChoiceBackgroundCss,
  multipleChoiceContainerCss,
  multipleChoiceCss,
  multipleChoiceLabelCss,
} from "./MultipleChoice.style";

interface MultipleChoiceProps {
  choiceIndex: number;
  choiceText: INode | null;
  currentReadAloudChoice: Letter | null;
  isCorrectChoice: boolean | null;
  isReadAloudButtonActive?: boolean;
  isStudentsChoice: boolean | null;
  setCurrentReadAloudChoice: Dispatch<SetStateAction<Letter | null>>;
  shouldHideQuizAnswers?: boolean;
}

export const MultipleChoice = ({
  choiceIndex,
  choiceText,
  currentReadAloudChoice,
  isCorrectChoice,
  isReadAloudButtonActive = false,
  isStudentsChoice,
  setCurrentReadAloudChoice,
  shouldHideQuizAnswers = false,
}: MultipleChoiceProps) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const {
    currentSpeech,
    isReadAloudModeActive,
    readAloudContentMode,
    currentMark,
  } = useReadAloudMode();

  const READ_ALOUD_CONTENT_MODE = ReadAloudContentModes.ANSWER;

  const editor = useMultipleChoiceEditor({
    content: choiceText,
    dataTestId: "TipTapQuizQuestionChoice-Editor",
    levelId,
  });

  // Dispatch the transaction and update the editor state (apply highlighting to selected words)
  useEffect(() => {
    if (editor) {
      const metaData =
        !isReadAloudModeActive ||
        readAloudContentMode !== READ_ALOUD_CONTENT_MODE ||
        currentReadAloudChoice !== ALPHABET.toUpperCase()[choiceIndex]
          ? {}
          : { ...currentMark };
      const transaction = editor.state.tr.setMeta(
        listenModeHighlightDecoratorPluginKey,
        metaData,
      );
      editor.view.dispatch(transaction);
    }
  }, [
    JSON.stringify(currentMark),
    isReadAloudModeActive,
    JSON.stringify(choiceText),
  ]);

  // Pause previous quiz answer if we're on a new one
  useEffect(() => {
    if (readAloudContentMode === READ_ALOUD_CONTENT_MODE && currentSpeech) {
      currentSpeech.pauseAudio();
    }
  }, [choiceIndex]);

  if (!editor || !choiceText) return null;

  const shouldHideCorrectAnswerLabel =
    !isCorrectChoice || shouldHideQuizAnswers;
  const shouldHideLabel = !isStudentsChoice && shouldHideCorrectAnswerLabel;

  const renderLabel = () => {
    if (shouldHideLabel) {
      return null;
    }

    const labelProps = {
      labelColor: isCorrectChoice ? "success" : "primary",
      status: isCorrectChoice ? "correct" : "primary",
      text:
        isCorrectChoice && !isStudentsChoice ? "Correct answer" : "Your answer",
    };

    return (
      <ActivityChoiceIndicator
        __cssFor={multipleChoiceLabelCss}
        labelColor={labelProps.labelColor}
        status={labelProps.status}
        text={labelProps.text}
      />
    );
  };

  const alphabeticAnswer = (ALPHABET.toUpperCase()[choiceIndex] ??
    "") as Letter;

  return (
    <li
      css={containerCss}
      data-testid={`quizQuestionuseMultipleChoiceEditorWrapper-${alphabeticAnswer}`}
    >
      <div
        css={[
          multipleChoiceContainerCss(isReadAloudButtonActive),
          multipleChoiceBackgroundCss(isCorrectChoice, shouldHideLabel),
        ]}
      >
        {renderLabel()}
        <div css={multipleChoiceCss}>
          <span css={choiceLetterCss}>{alphabeticAnswer}</span>
          <span css={choiceTextCss}>
            <ReadOnlyEditorContent
              editor={editor}
              data-testid={`readOnlyEditorMultipleChoiceContent-${alphabeticAnswer}`}
            />
          </span>
        </div>
      </div>
      {isReadAloudButtonActive && (
        <ReadAloudButton
          buttonContentMode={READ_ALOUD_CONTENT_MODE}
          position={alphabeticAnswer}
          setCurrentReadAloudChoice={() =>
            setCurrentReadAloudChoice(alphabeticAnswer)
          }
          text={choiceText}
          type={ButtonTypes.answer}
        />
      )}
    </li>
  );
};

export default MultipleChoice;
